import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from './api';
import HamburgerMenu from './HamburgerMenu';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        border: isDark ? '#444' : '#e2e8f0',
        link: isDark ? '#6cb2f7' : '#007bff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
        containerBg: isDark ? '#121212' : '#f4f6f8',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        success: isDark ? '#4caf50' : '#28a745',
        warning: isDark ? '#ff9800' : '#ffc107',
        secondary: isDark ? '#9e9e9e' : '#64748b',
    };

    return {
        pageWrapper: { fontFamily: 'system-ui, -apple-system, sans-serif', minHeight: '100vh', backgroundColor: colors.containerBg },
        pageHeader: {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px',
        },
        pageTitle: { fontSize: '24px', fontWeight: '600', margin: 0, color: colors.text },
        statusBar: {
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px 24px',
            padding: '16px 24px', backgroundColor: colors.cardBg, borderBottom: `1px solid ${colors.border}`,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
        },
        statusBarItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' },
        statusBarLabel: { color: colors.secondary, fontWeight: '500' },
        statusBarValue: { color: colors.text, fontWeight: '600' },
        navLink: { display: 'inline-block', marginBottom: '16px', textDecoration: 'none', color: colors.link, fontSize: '14px', fontWeight: '500' },
        container: {
            maxWidth: '1200px', margin: '0 auto', padding: '24px',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(400px, 100%), 1fr))',
            gap: '16px',
        },
        cardSection: {
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: colors.cardBg,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
            alignSelf: 'start',
        },
        cardHeader: {
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
            padding: '16px 20px', cursor: 'pointer',
            borderBottom: isDark ? 'none' : '1px solid #f1f5f9',
        },
        cardTitle: { fontSize: '16px', fontWeight: '600', color: colors.text },
        caret: { marginRight: '8px', transition: 'transform 0.2s ease', fontSize: '12px', color: colors.secondary },
        progressBarContainer: {
            position: 'relative', minWidth: '100px', flexShrink: 0,
            backgroundColor: isDark ? '#333' : '#e9ecef', borderRadius: '8px', overflow: 'hidden', height: '24px',
        },
        progressBar: { height: '100%', borderRadius: '8px', transition: 'width 0.3s ease' },
        progressText: { position: 'absolute', width: '100%', textAlign: 'center', lineHeight: '24px', fontSize: '12px', fontWeight: '600', color: colors.text },
        cardBody: { padding: '16px 20px' },
        outstandingLabel: { fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.secondary, marginBottom: '10px' },
        outstandingList: { listStyleType: 'none', padding: 0, margin: 0 },
        outstandingItem: {
            padding: '10px 12px', marginBottom: '6px', borderRadius: '8px',
            backgroundColor: isDark ? 'rgba(255,193,7,0.08)' : 'rgba(255,193,7,0.12)',
            borderLeft: `3px solid ${colors.warning}`,
            fontSize: '13px', color: colors.text,
        },
        allComplete: { padding: '12px', color: colors.success, fontSize: '14px', fontWeight: '500' },
        controlButton: {
            padding: '8px 12px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
            border: `1px solid ${colors.border}`, borderRadius: '8px',
            backgroundColor: colors.cardBg, color: colors.text,
        },
        success: colors.success,
        warning: colors.warning,
        link: colors.link,
    };
};

function InProgressReportPage({ theme }) {
    const styles = getStyles(theme);
    const [reportData, setReportData] = useState([]);
    const [inspectionInfo, setInspectionInfo] = useState(null);
    // New state to track which cards are open
    const [openCards, setOpenCards] = useState(new Set());
    const { inspectionId } = useParams();

    useEffect(() => {
        if (inspectionId) {
            Promise.all([
                api.get(`/inspections/${inspectionId}/`),
                api.get(`/inspections/${inspectionId}/progress-report/`)
            ]).then(([inspectionRes, reportRes]) => {
                setInspectionInfo(inspectionRes.data);
                // Ensure we have an array for report data
                const reportData = Array.isArray(reportRes.data) ? reportRes.data : [];
                setReportData(reportData);
                // Sections are now collapsed by default
            }).catch(err => {
                console.error("Error fetching report data:", err);
                setReportData([]); // Set to empty array on error
            });
        }
    }, [inspectionId]);

    // New handler to toggle the collapsible sections
    const toggleCard = (card) => {
        setOpenCards(prevOpen => {
            const newOpen = new Set(prevOpen);
            if (newOpen.has(card)) {
                newOpen.delete(card);
            } else {
                newOpen.add(card);
            }
            return newOpen;
        });
    };

    // New handlers for master controls
    const expandAll = () => {
        const allCardKeys = new Set(Array.isArray(reportData) ? reportData.map(card => card.card) : []);
        setOpenCards(allCardKeys);
    };

    const collapseAll = () => {
        setOpenCards(new Set());
    };

    if (!inspectionInfo) {
        return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'system-ui' }}>Loading Report...</div>;
    }

    const inspectionType = inspectionInfo.checklist_type || inspectionInfo.phase || '—';
    const completedCards = reportData.filter(c => c.percent_complete === 100).length;
    const totalCards = reportData.length;
    const cardsRemaining = totalCards - completedCards;

    return (
        <div style={styles.pageWrapper}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
                <div style={styles.pageHeader}>
                    <HamburgerMenu theme={theme} />
                    <h1 style={styles.pageTitle}>Progress Report</h1>
                    <div style={{ width: '45px' }}></div>
                </div>

                <Link to={`/inspection/${inspectionId}`} style={styles.navLink}>&larr; Back to Checklist</Link>

                <div style={styles.statusBar}>
                    <div style={styles.statusBarItem}>
                        <span style={styles.statusBarLabel}>BUNO</span>
                        <span style={styles.statusBarValue}>{inspectionInfo.buno}</span>
                    </div>
                    <div style={styles.statusBarItem}>
                        <span style={styles.statusBarLabel}>Type</span>
                        <span style={styles.statusBarValue}>{inspectionType}</span>
                    </div>
                    <div style={styles.statusBarItem}>
                        <span style={styles.statusBarLabel}>Remaining</span>
                        <span style={styles.statusBarValue}>{cardsRemaining} card{cardsRemaining !== 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button onClick={expandAll} style={styles.controlButton}>Expand All</button>
                        <button onClick={collapseAll} style={styles.controlButton}>Collapse All</button>
                    </div>
                </div>


                <div style={styles.container}>
                    {Array.isArray(reportData) && reportData.map(card => {
                        const isOpen = openCards.has(card.card);
                        const isComplete = card.percent_complete === 100;
                        return (
                            <div key={card.card} style={styles.cardSection}>
                                <div style={styles.cardHeader} onClick={() => toggleCard(card.card)}>
                                    <div style={styles.cardTitle}>
                                        <span style={{ ...styles.caret, display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>&#9656;</span>
                                        Card {card.card}
                                    </div>
                                    <div style={styles.progressBarContainer}>
                                        <div style={styles.progressText}>{card.percent_complete}%</div>
                                        <div style={{
                                            ...styles.progressBar,
                                            width: `${Math.min(card.percent_complete, 100)}%`,
                                            backgroundColor: isComplete ? styles.success : styles.link
                                        }}></div>
                                    </div>
                                </div>
                                {isOpen && (
                                    <div style={styles.cardBody}>
                                        {card.outstanding_items?.length > 0 ? (
                                            <>
                                                <div style={styles.outstandingLabel}>
                                                    {card.outstanding_items.length} item{card.outstanding_items.length !== 1 ? 's' : ''} remaining
                                                </div>
                                                <ul style={styles.outstandingList}>
                                                    {card.outstanding_items.map((item, index) => (
                                                        <li key={index} style={styles.outstandingItem}>{item}</li>
                                                    ))}
                                                </ul>
                                            </>
                                        ) : (
                                            <div style={styles.allComplete}>✓ All items completed</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default InProgressReportPage;
